import { Drawer, ErrorBoundary, SidebarShell, TopBar } from "@closer/ui";
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router";

import { CollapseIcon, ExpandIcon, MenuIcon } from "../components/icons";
import { useIsDesktop } from "../lib/useIsDesktop";
import { BaglantiUyarisi } from "./BaglantiUyarisi";
import { NewContractAction } from "./NewContractAction";
import { NotificationsPanel } from "./NotificationsPanel";
import { PendingTasksPanel } from "./PendingTasksPanel";
import { ProfileMenu } from "./ProfileMenu";
import { SearchOpener } from "./SearchOpener";
import { readSidebarCollapsed, Sidebar, writeSidebarCollapsed } from "./Sidebar";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

/**
 * Rota değiştiğinde ODAĞI ve BAŞLIĞI taşır.
 *
 * ### ⚠ Neden gerekli: SPA'da gezinme sessizdir
 *
 * Tarayıcı tam sayfa yüklemede yeni belgeyi duyurur; istemci tarafı yönlendirmede bunu
 * kimse yapmaz. Denetimde ölçüldü (5 Eylül): kabukta ne `document.title` güncellemesi, ne
 * rota değişiminde `.focus()` vardı. İki sonucu:
 *
 * 1. Ekran okuyucu kullanıcısı sayfanın değiştiğini HİÇ duymuyordu — aynı sayfada
 *    kaldığını sanıyordu.
 * 2. Klavye kullanıcısı her geçişte odağı kabuğun başında buluyor ve marka + beş gezinme
 *    öğesi + daraltma + altı üst bar yuvası = ~13 durağı yeniden geçiyordu.
 *
 * ### ⚠ Odak `<main>`'e taşınıyor, ilk öğeye DEĞİL
 *
 * İlk odaklanabilir öğeye atlamak, ekran okuyucuya sayfanın başını değil o öğeyi okutur.
 * `tabIndex={-1}` taşıyan kap odaklandığında okuyucu bölgenin adını ve içeriğin başını
 * duyuruyor; klavye kullanıcısının bir sonraki Tab'ı da içeriğin ilk öğesine gidiyor.
 *
 * ### ⚠ İLK ÇİZİMDE ÇALIŞMIYOR, bilerek
 *
 * Sayfa yeni yüklendiğinde odağı çalmak, kullanıcının adres çubuğundaki ya da tarayıcı
 * arayüzündeki yerini bozar. Yalnız rota DEĞİŞİMİNDE devreye giriyor.
 */
function useRotaDuyurusu(hedef: React.RefObject<HTMLElement | null>) {
  const { pathname } = useLocation();
  const ilkCizim = useRef(true);

  useEffect(() => {
    if (ilkCizim.current) {
      ilkCizim.current = false;
      return;
    }
    hedef.current?.focus();
  }, [pathname, hedef]);
}

/** Sekme başlığının sabit kuyruğu — hangi üründe olduğunu her sekmede söylüyor. */
const BASLIK_KUYRUGU = "Closer";

/**
 * Sekme başlığını sayfanın KENDİ `<h1>`'inden yazar.
 *
 * ### ⚠ Neden `<h1>`, neden bir rota→başlık kaydı DEĞİL
 *
 * Kabukta zaten beş bölüm kaydı var (`navigation`, `contractSections`,
 * `organizationSections`, `subscriptionSections`, `contractDetailSections`). Altıncı bir
 * eşleme yazmak, aynı bilginin ikinci kaynağı olurdu ve gün gelip ayrışırdı — üstelik o
 * kayıtlar GEZİNME adlarını tutuyor, sayfanın kendi başlığını değil: sözleşme detayının
 * başlığı sözleşmenin adı, "Sözleşmeler" değil.
 *
 * `PageHeader` her ekranda tek bir `<h1>` garanti ediyor (`packages/ui/src/PageHeader.tsx`)
 * ve o başlık sayfanın kanonik adı. Kaynak zaten orada; buradaki iş onu aynalamak.
 *
 * ### ⚠ Neden `MutationObserver`
 *
 * Başlıkların çoğu SUNUCUDAN geliyor ve `<h1>` ilk çizimde henüz yok (sözleşme adı, müvekkil
 * adı). Tek seferlik okuma "Closer" bırakırdı. Gözlemci, başlık geldiğinde ve değiştiğinde
 * yazıyor; rota değişiminde yeniden kuruluyor.
 */
function useSekmeBasligi(kap: React.RefObject<HTMLElement | null>) {
  const { pathname } = useLocation();

  useEffect(() => {
    const kok = kap.current;
    if (!kok) return;

    const yaz = () => {
      const baslik = kok.querySelector("h1")?.textContent?.trim();
      document.title = baslik ? `${baslik} · ${BASLIK_KUYRUGU}` : BASLIK_KUYRUGU;
    };

    yaz();
    const gozlemci = new MutationObserver(yaz);
    gozlemci.observe(kok, { childList: true, subtree: true, characterData: true });
    return () => gozlemci.disconnect();
  }, [pathname, kap]);
}

export function AppLayout() {
  /** Daraltma tercihi açılışta okunuyor: kullanıcı her yüklemede yeniden daraltmasın. */
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const desktop = useIsDesktop();
  const anaIcerik = useRef<HTMLElement>(null);
  useRotaDuyurusu(anaIcerik);
  useSekmeBasligi(anaIcerik);
  const [drawerOpen, setDrawerOpen] = useState(false);

  function toggleSidebar() {
    setCollapsed((onceki) => {
      const yeni = !onceki;
      writeSidebarCollapsed(yeni);
      return yeni;
    });
  }

  /**
   * ⚠ AYNI `<Sidebar>` iki kapta render ediliyor — masaüstünde `<aside>`, mobilde
   * `<Drawer>` (CLOS-97 `[058]`). İki ayrı liste yazılsaydı kartın *"mobil drawer ayrı ve
   * daha gevşek bir güvenlik görünürlük kuralı üretmez"* kriteri ihlal edilirdi: biri
   * güncellenip diğeri unutulduğunda mobilde yetkisiz bir öğe görünürdü.
   */
  const gezinme = (
    <Sidebar collapsed={desktop && collapsed} onNavigate={() => setDrawerOpen(false)} />
  );

  /**
   * Sidebar kontrolü BURADA geçici olarak duruyor. Kanonik yeri Üst Bar'ın ilk öğesi
   * (`[059]` CLOS-98) ve orası geldiğinde taşınacak; şimdi kaldırılsaydı ne daraltma
   * tercihi ne de mobil gezinme erişilebilir olurdu — kartların *"daraltma işlevleri
   * kaybolmaz"* ve *"kritik navigasyon kaybolmaz"* kriterleri bunu yasaklıyor.
   *
   * ⚠ Erişilebilir adlar ("Menüyü daralt" / "Menüyü genişlet" / "Menüyü aç") SABİT: üç
   * ayrı test bu adlarla tıklıyor ve ad değişimi sessizce kırmızı yakar.
   *
   * ⚠ Kontrol `<nav aria-label="Ana gezinme">` DIŞINDA duruyor. İçeri alınsaydı masaüstü
   * ve mobil drawer'ın gezinme öğeleri ayrışırdı; bir test ikisini birebir karşılaştırıyor
   * ve kartın *"mobil drawer ayrı bir kural üretmez"* kriteri buna dayanıyor.
   */
  const sidebarKontrolu = (
    <button
      type="button"
      onClick={() => (desktop ? toggleSidebar() : setDrawerOpen(true))}
      aria-label={desktop ? (collapsed ? "Menüyü genişlet" : "Menüyü daralt") : "Menüyü aç"}
      aria-expanded={desktop ? !collapsed : drawerOpen}
      className={
        desktop
          ? "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-fg-soft transition-colors hover:bg-muted hover:text-fg-heading focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          : "rounded-xl p-2 text-fg-muted transition-colors hover:bg-muted hover:text-fg-heading focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      }
    >
      {desktop ? collapsed ? <ExpandIcon /> : <CollapseIcon /> : <MenuIcon />}
      {/* Etiket yalnız geniş masaüstü sidebar'ında; daraltılmışken ad `aria-label`'da kalıyor. */}
      {desktop && !collapsed ? <span>Menüyü Daralt</span> : null}
    </button>
  );

  /**
   * Marka kilidi — sidebar'ın tepesinde, üst barla aynı yükseklikte. Daraltılmışken
   * yalnız monogram kalıyor: wordmark 64px'e sığmıyor ve kırpılmış bir logo, bozuk bir
   * logodur.
   */
  const marka = (
    <Link
      to="/app"
      aria-label="Closer ana sayfa"
      className="flex h-16 items-center gap-2.5 rounded-xl px-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-on-inverse"
      >
        C
      </span>
      {collapsed ? null : (
        <span
          aria-hidden="true"
          className="text-lg font-bold tracking-[0.14em] text-fg-heading uppercase"
        >
          Closer
        </span>
      )}
    </Link>
  );

  return (
    <div className="flex min-h-screen bg-canvas">
      {/*
        ⚠ ATLAMA BAĞLANTISI — kabuktaki İLK odaklanabilir öğe olmak zorunda.
        Görünmez ama odaklanınca beliriyor (`sr-only focus:not-sr-only`): fare kullanıcısını
        rahatsız etmeden klavye kullanıcısına ~13 duraklık gezinmeyi atlama yolu veriyor.
      */}
      <a
        href="#ana-icerik"
        className="sr-only rounded-md bg-primary px-4 py-2 text-sm font-semibold text-on-inverse focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50"
      >
        Ana içeriğe geç
      </a>
      {desktop ? (
        <SidebarShell collapsed={collapsed} logo={marka} footer={sidebarKontrolu}>
          {gezinme}
        </SidebarShell>
      ) : (
        /**
         * Masaüstünde `Drawer` HİÇ render edilmiyor. Sadece CSS ile gizlenseydi ekran
         * büyürken açık kalan bir drawer görünmez olur ama odak kapanı AKTİF kalırdı —
         * klavye kullanıcısı göremediği bir panelin içine hapsolurdu.
         */
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Ana gezinme">
          {gezinme}
        </Drawer>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          slots={{
            /* Masaüstünde kontrol kenar çubuğunda; iki yerde birden çizmek karışıklık olurdu. */
            sidebarControl: desktop ? null : sidebarKontrolu,
            /* Kanonik seçici `[060]` CLOS-99'da doğdu; kabuk yalnız yuvaya yerleştiriyor. */
            workspace: <WorkspaceSwitcher />,
            /* Arama açıcısı `[061]` CLOS-100; sonuç üretmiyor, kanonik konumu burası. */
            search: <SearchOpener />,
            /* Yeni Sözleşme birincil aksiyonu `[062]` CLOS-101; yalnız merkezi açıyor. */
            newContract: <NewContractAction />,
            /* Bekleyen İşlemler kabuğu `[063]` CLOS-102; karar aksiyonu SUNMUYOR. */
            pendingTasks: <PendingTasksPanel />,
            /* Bildirim kabuğu `[064]` CLOS-103; hatası diğer yuvaları durdurmuyor. */
            notifications: <NotificationsPanel />,
            /* Davetler BİLEREK yok: kart "Davetler kalıcı Üst Bar öğesi değildir" diyor;
               kanonik girişi `[065]` Profil menüsü. */
            /* Profil menüsü `[065]` CLOS-104; hesap özeti, kanonik kayıt ve güvenli çıkış. */
            profile: <ProfileMenu />,
          }}
        />
        {/**
         * İçerik genişliği sınırlanıyor: 27" bir ekranda tabloların satırları iki metreye
         * yayılıyor ve göz satır başını kaybediyordu. Ölçü onaylı tasarımlardaki içerik
         * sütununun genişliği.
         */}
        <main
          id="ana-icerik"
          ref={anaIcerik}
          /**
           * ⚠ `tabIndex={-1}`: fare/klavye sırasına GİRMİYOR ama programla odaklanabiliyor.
           * Atlama bağlantısının ve rota değişiminin hedefi burası.
           */
          tabIndex={-1}
          className="min-w-0 flex-1 px-5 py-7 outline-none sm:px-8"
        >
          <div className="mx-auto w-full max-w-[1440px]">
            {/*
              ⚠ BAĞLANTI ŞERİDİ KABUKTA, sayfalarda DEĞİL (CLOS-1278).

              Her ekrana tek tek koysaydık biri unutulurdu ve unutulanın kritik bir yüzey
              olmayacağının garantisi yok — `apiClient` kapısının tek yerde durmasıyla aynı
              gerekçe. Kabukta durduğu için hangi sayfada olursa olsun görünüyor.
            */}
            <BaglantiUyarisi />
            {/*
              ⚠ ÇÖKME BİR YERE YAZILIYOR — ölçülerek eklendi (4 Eylül 2026).

              `onError` hiç bağlanmamıştı: ekranı kaplayan bir çizim hatası hiçbir iz
              bırakmıyordu. Dev'de Müvekkiller ekranı bu kutuyla çöktü ve ne konsolda ne
              sunucuda tek satır yoktu — uçların hepsi 200 dönüyordu, yani sebep yalnız
              istemcideydi ve ölçülemiyordu. Teşhis edilemeyen bir çökme,
              düzeltilemeyen bir çökmedir.

              ⚠ EKRANDA HÂLÂ GÖSTERİLMİYOR: kutunun kendi gerekçesi geçerli — kullanıcıya
              yığın izi basılmaz (`[067]`). Konsol geliştiriciye ait bir yüzey; kullanıcı
              yine yalnız yapabileceği şeyi görüyor.
            */}
            <ErrorBoundary
              onError={(hata, bilgi) => {
                /*
                  ⚠ `no-console` BİLEREK atlanıyor. Kural başıboş hata ayıklama çıktısını
                  hedefliyor; burada tek geliştirici yüzeyi konsol ve alternatifi hatanın
                  hiç kaydedilmemesi. Ürünün istemci tarafında bugün telemetri YOK — o
                  gelince bu çağrı oraya taşınır ve istisna kalkar.
                */
                // eslint-disable-next-line no-console
                console.error(
                  `[closer] çizim hatası: ${hata.name}: ${hata.message}`,
                  bilgi.componentStack,
                );
              }}
            >
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
